import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import { langCodeToJapanese } from "@/lib/chillnn/lang-code";
import type { LangCode, PlanI18n_DesignPageQuery } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { ToastAction } from "@/components/ui/toast/toast";

import * as Shared from "../../../../../_shared";
import { usePlanI18nArticleUpdateMutation } from "../../repository/mutation/use-plan-article-update-mutation";

type Props = {
	hotelID: string;
	planID: string;
	langCode: LangCode;
	serverModel: PlanI18n_DesignPageQuery;
};

/**
 * @todo キャッシュされているので許容できるが、２回取得の処理が走っているので対応する
 */
export const useInitialized = ({ planID, hotelID, langCode, serverModel }: Props) => {
	const { article } = serverModel.planI18n;
	const { onUpdate: _onUpdate } = usePlanI18nArticleUpdateMutation({ hotelID, planID, langCode });

	const navigate = useNavigate();
	const planListPath = generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.PLAN, { hotelID });

	const onNavigateToPlanList = useCallback(() => {
		navigate(planListPath);
	}, [planListPath, navigate]);

	const onUpdateSuccessCallback = useCallback(() => {
		const langCodeText = langCodeToJapanese(langCode);

		notifyUserEventInfo({
			title: `プラン記事（${langCodeText}）を保存しました`,
			action: (
				<ToastAction onClick={onNavigateToPlanList} altText="一覧に戻る">
					一覧に戻る
				</ToastAction>
			),
		});
	}, [langCode, onNavigateToPlanList]);

	const { articleHTML, onSetArticleHTML, isDirty, isValid, onUpdate } =
		Shared.Model.UseCase.useCreateMagazineEditorForI18nModel({
			article,
			onUpdate: _onUpdate,
			onUpdateSuccessCallback,
		});

	return {
		serverModel,
		articleHTML,
		onSetArticleHTML,
		isDirty,
		isValid,
		onUpdate,
	};
};
